import React from "react";
import { Grid, Paper, Stack, Typography, Box, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BoltIcon from "@mui/icons-material/Bolt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const LINK_BY_LABEL = {
  "Total Courses":        "/admin/course-list",
  "Groups":               "/admin/resource-allocation",
  "Active Batches":       "/admin/resource-allocation",
  "Active Classes":       "/admin/resource-allocation",
  "Enrolled Students":    "/admin/students",
  "Worksheets Assigned":  "/admin/resource-allocation",
  "Worksheets Completed": "/admin/resource-allocation",
  "Total App Users":       "/admin/mobile-users",
  "Active Subscriptions":  "/admin/mobile-users",
  "Trial Users":           "/admin/mobile-users",
  "Payment Failures":      "/admin/mobile-users",
};

const BADGE_BY_LABEL = {
  "Total Courses":        { Icon: MenuBookIcon,    bg: "#ede9fe", fg: "#7c3aed" },
  "Groups":               { Icon: GroupsIcon,      bg: "#dcfce7", fg: "#16a34a" },
  "Active Batches":       { Icon: GroupsIcon,      bg: "#dcfce7", fg: "#16a34a" },
  "Active Classes":       { Icon: GroupsIcon,      bg: "#dcfce7", fg: "#16a34a" },
  "Enrolled Students":    { Icon: PersonIcon,      bg: "#dbeafe", fg: "#2563eb" },
  "Worksheets Assigned":  { Icon: DescriptionIcon, bg: "#fef3c7", fg: "#d97706" },
  "Worksheets Completed": { Icon: CheckCircleIcon, bg: "#cffafe", fg: "#0891b2" },
  "Total App Users":      { Icon: PhoneIphoneIcon,  bg: "#dbeafe", fg: "#2563eb" },
  "Active Subscriptions": { Icon: CheckCircleIcon,  bg: "#dcfce7", fg: "#16a34a" },
  "Trial Users":          { Icon: BoltIcon,         bg: "#ede9fe", fg: "#7c3aed" },
  "Payment Failures":     { Icon: WarningAmberIcon, bg: "#fde2e2", fg: "#dc2626" },
};

const LG_BY_COUNT = { 1: 12, 2: 6, 3: 4, 4: 3, 5: 2.4, 6: 2 };

const DEFAULT_CARDS = [
  { label: "Total Courses", value: "—", delta: 0, delta_label: "" },
  { label: "Groups", value: "—", delta: 0, delta_label: "" },
  { label: "Enrolled Students", value: "—", delta: 0, delta_label: "" },
  { label: "Worksheets Assigned", value: "—", delta: 0, delta_label: "" },
  { label: "Worksheets Completed", value: "—", delta: 0, delta_label: "" },
];

export default function KpiCards({ cards }) {
  const navigate = useNavigate();
  const items = cards || DEFAULT_CARDS;
  return (
    <Grid container spacing={2}>
      {items.map((card) => {
        const cfg = BADGE_BY_LABEL[card.label] || BADGE_BY_LABEL["Total Courses"];
        const Icon = cfg.Icon;
        const delta = card.delta ?? 0;
        const up = delta >= 0;
        // Payment failures: the API sends previous − current, so a positive number is good
        // news. Say it in words instead of a green "↑" that reads like more failures.
        const lowerIsBetter = card.label === "Payment Failures";
        const since = String(card.delta_label || "").replace(/^from /, "than the ");
        const trendText = card.delta_display || (delta === 0
          ? "No change"
          : lowerIsBetter
            ? `${Math.abs(delta)} ${delta > 0 ? "fewer" : "more"} ${since}`.trim()
            : `${Math.abs(delta)} ${card.delta_label}`);
        const trendColor = delta === 0 ? "#6B7280" : up ? "#16a34a" : "#dc2626";
        const href = card.href || LINK_BY_LABEL[card.label];
        return (
          <Grid item xs={12} sm={6} md={4} lg={LG_BY_COUNT[items.length] || 2.4} key={card.label}>
            <Paper
              onClick={href ? () => navigate(href) : undefined}
              sx={{
                p: 2,
                borderRadius: 3,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                border: "1px solid #eef0f4",
                cursor: href ? "pointer" : "default",
                "&:hover": href ? { boxShadow: "0 6px 20px rgba(0,0,0,0.08)" } : {},
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 48, height: 48, borderRadius: "50%",
                    bgcolor: cfg.bg, color: cfg.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <Icon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} noWrap>{card.label}</Typography>
                    {card.hint && (
                      <Tooltip title={card.hint} arrow placement="top">
                        <InfoOutlinedIcon sx={{ fontSize: 13, color: "#9aa1ac", cursor: "help", flexShrink: 0 }} />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, color: "#111827" }}>
                    {typeof card.value === "number" ? card.value.toLocaleString('en-GB') : card.value}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {delta !== 0 && !lowerIsBetter && (up ? (
                      <TrendingUpIcon sx={{ fontSize: 14, color: trendColor }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 14, color: trendColor }} />
                    ))}
                    <Typography variant="caption" sx={{ color: trendColor, fontWeight: 700, lineHeight: 1.3 }}>
                      {trendText}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
}
